import * as React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface NumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type"> {
  value: number | null | undefined;
  onValueChange: (value: number) => void;
  /** Value to set when the field is cleared (default: 0) */
  emptyValue?: number;
  // PR-AS: explicitly forbid `onChange` at the call site. The Omit above
  // removes it from the type signature, but TypeScript's loose Omit +
  // JSX-spread (line 64 below) lets callers pass it anyway, which then
  // OVERRIDES the internal handleChange and bypasses NumberInput's
  // displayValue state — silently corrupting controlled-input behavior.
  // The `onChange?: never` declaration makes the compiler reject any
  // `<NumberInput onChange={...}>` site with a clear error.
  onChange?: never;
}

/**
 * Number input that avoids the sticky-zero bug.
 * Stores display value as string internally, parses on blur.
 * Allows clearing the field, typing without leading zeros,
 * and handles negative/decimal numbers.
 */
const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ value, onValueChange, emptyValue = 0, className, onFocus, onBlur, ...props }, ref) => {
    const [displayValue, setDisplayValue] = React.useState(() => String(value ?? emptyValue));
    const [focused, setFocused] = React.useState(false);

    // Sync display value from prop when not focused
    React.useEffect(() => {
      if (!focused) {
        setDisplayValue(String(value ?? emptyValue));
      }
    }, [value, emptyValue, focused]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      // Select all on focus so typing replaces the value
      requestAnimationFrame(() => e.target.select());
      onFocus?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      // Allow empty, negative sign, decimal point, and digits
      if (raw === "" || raw === "-" || raw === "." || raw === "-." || /^-?\d*\.?\d*$/.test(raw)) {
        setDisplayValue(raw);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      const parsed = parseFloat(displayValue);
      const final = isNaN(parsed) ? emptyValue : parsed;
      setDisplayValue(String(final));
      onValueChange(final);
      onBlur?.(e);
    };

    return (
      <Input
        ref={ref}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn("[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none", className)}
        {...props}
      />
    );
  }
);
NumberInput.displayName = "NumberInput";

export { NumberInput };
